/**
 * Sidebar.jsx - Collapsible sidebar navigation.
 *
 * Renders the brand logo, grouped navigation items, a theme toggle,
 * and user info with sign-out at the bottom.
 *
 * Props:
 *   open      {boolean}   - Whether the sidebar is expanded (false = collapsed on mobile).
 *   onClose   {function}  - Called when the sidebar should close (mobile overlay dismiss).
 *   darkMode  {boolean}   - Current dark-mode state.
 *   onToggleTheme {function} - Toggles dark/light mode.
 *   user      {object}    - User object with `name` and `email` properties.
 *   onSignOut {function}  - Sign-out handler.
 *
 * Navigation items are defined internally and use react-router-dom NavLink
 * for active-state highlighting.
 */

import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Search,
  Users,
  Package,
  ShoppingCart,
  ShieldCheck,
  BarChart3,
  Upload,
  Settings,
  LogOut,
  Sun,
  Moon,
  X,
  ChevronLeft,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function Sidebar({
  open,
  onClose,
  darkMode,
  onToggleTheme,
  user,
  onSignOut,
}) {
  const location = useLocation();

  const navGroups = [
    {
      heading: "Work",
      items: [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/sourcing", label: "Sourcing", icon: Search },
        { to: "/vendors", label: "Vendors", icon: Users },
        { to: "/products", label: "Products", icon: Package },
        { to: "/orders", label: "Orders", icon: ShoppingCart },
        { to: "/quality", label: "Quality", icon: ShieldCheck },
      ],
    },
    {
      heading: "Insight",
      items: [{ to: "/reports", label: "Reports", icon: BarChart3 }],
    },
    {
      heading: "Setup",
      items: [
        ...(user?.username === "superadmin"
          ? [
              { to: "/import", label: "Import", icon: Upload },
              { to: "/admin", label: "Admin", icon: Settings },
            ]
          : []),
      ],
    },
  ];

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          style={styles.backdrop}
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        style={{
          ...styles.sidebar,
          transform: open ? "translateX(0)" : "translateX(-100%)",
        }}
        aria-label="Main navigation"
      >
        {/* Brand */}
        <div style={styles.brandRow}>
          <div style={styles.brand}>
            <img src="/ysu-logo.png" alt="VMS" style={{ width: 32, height: 32 }} />
            <span style={styles.brandText}>Vendor Management System</span>
          </div>
          <button
            onClick={onClose}
            style={styles.iconBtn}
            aria-label="Close sidebar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation groups */}
        <nav style={styles.nav}>
          {navGroups.map((group) => (
            <div key={group.heading} style={styles.group}>
              <div style={styles.groupHeading}>{group.heading}</div>
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive =
                  location.pathname === item.to ||
                  location.pathname.startsWith(item.to + "/");

                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={onClose}
                    style={{
                      ...styles.navItem,
                      ...(isActive ? styles.navItemActive : {}),
                    }}
                  >
                    <Icon
                      size={17}
                      style={{
                        color: isActive
                          ? "var(--accent)"
                          : "var(--ink-3)",
                        flexShrink: 0,
                      }}
                    />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Bottom section */}
        <div style={styles.bottom}>
          {/* Theme toggle */}
          <button
            onClick={onToggleTheme}
            style={styles.themeBtn}
            aria-label={
              darkMode ? "Switch to light mode" : "Switch to dark mode"
            }
          >
            {darkMode ? <Sun size={16} /> : <Moon size={16} />}
            <span>{darkMode ? "Light mode" : "Dark mode"}</span>
          </button>

          {/* User info */}
          {user && (
            <div style={styles.userCard}>
              <div style={styles.avatar}>
                {(user.name || user.email || "?")[0].toUpperCase()}
              </div>
              <div style={styles.userInfo}>
                <div style={styles.userName}>
                  {user.name || user.email || "User"}
                </div>
                {user.email && (
                  <div style={styles.userEmail}>{user.email}</div>
                )}
              </div>
              <button
                onClick={onSignOut}
                style={styles.signOutBtn}
                aria-label="Sign out"
                title="Sign out"
              >
                <LogOut size={16} />
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const SIDEBAR_WIDTH = 240;

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 900,
    background: "rgba(0, 0, 0, 0.35)",
  },
  sidebar: {
    position: "fixed",
    top: 0,
    left: 0,
    bottom: 0,
    width: SIDEBAR_WIDTH,
    zIndex: 950,
    display: "flex",
    flexDirection: "column",
    background: "var(--panel)",
    borderRight: "1px solid var(--line)",
    transition: "transform 0.22s ease",
    overflow: "hidden",
  },
  brandRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 16px 12px",
    borderBottom: "1px solid var(--line-2)",
    flexShrink: 0,
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  brandText: {
    fontWeight: 700,
    fontSize: 16,
    color: "var(--ink)",
    letterSpacing: "-0.02em",
  },
  iconBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "var(--ink-3)",
    padding: 4,
    display: "inline-flex",
    borderRadius: "var(--radius-sm)",
  },
  nav: {
    flex: 1,
    overflowY: "auto",
    padding: "8px 0",
  },
  group: {
    padding: "4px 0",
  },
  groupHeading: {
    padding: "8px 18px 5px",
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--ink-3)",
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 18px 8px 15px",
    fontSize: 13,
    fontWeight: 550,
    color: "var(--ink-2)",
    textDecoration: "none",
    borderRadius: 0,
    borderLeft: "3px solid transparent",
    transition: "background 0.12s, color 0.12s, border-color 0.12s",
  },
  navItemActive: {
    color: "var(--accent)",
    background: "var(--accent-soft)",
    borderLeft: "3px solid var(--accent)",
    fontWeight: 650,
  },
  bottom: {
    borderTop: "1px solid var(--line-2)",
    padding: "10px 12px 12px",
    flexShrink: 0,
  },
  themeBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "8px 8px",
    fontSize: 12.5,
    fontWeight: 550,
    color: "var(--ink-3)",
    background: "none",
    border: "none",
    borderRadius: "var(--radius-sm)",
    cursor: "pointer",
    textAlign: "left",
  },
  userCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 6px 4px",
    marginTop: 2,
  },
  avatar: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: "var(--accent-soft)",
    color: "var(--accent)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 13,
    flexShrink: 0,
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
  },
  userName: {
    fontSize: 12.5,
    fontWeight: 600,
    color: "var(--ink)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  userEmail: {
    fontSize: 11,
    color: "var(--ink-3)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  signOutBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "var(--ink-3)",
    padding: 4,
    display: "inline-flex",
    borderRadius: "var(--radius-sm)",
    flexShrink: 0,
  },
};
